import { useEffect, useState, useRef } from "react";
import { Upload, Loader2, Check, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { resolveImg } from "@/lib/util";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function MediaPicker({ open, onClose, onSelect, type = "image" }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get(`/media?type=${type}`).then((r) => setMedia(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); }, [open, type]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setMedia([data, ...media]);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail, "Upload failed"));
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="media-picker">
        <DialogHeader><DialogTitle>Select {type === "pdf" ? "PDF" : "Image"} from Media Library</DialogTitle></DialogHeader>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">{media.length} file(s)</p>
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="inline-flex items-center gap-2 bg-navy text-ivory px-4 py-2 rounded-sm text-sm hover:bg-navy-light disabled:opacity-60" data-testid="media-picker-upload">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload New
          </button>
          <input ref={fileRef} type="file" className="hidden" accept={type === "pdf" ? "application/pdf" : "image/*"} onChange={upload} />
        </div>
        {loading ? <div className="py-12 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div> :
          media.length === 0 ? <div className="py-12 text-center text-slate-400">No files yet. Upload one to begin.</div> :
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {media.map((m) => (
              <button key={m.id} onClick={() => { onSelect(m); onClose(); }}
                className="group relative border border-slate-200 rounded-sm overflow-hidden hover:border-brass aspect-square bg-slate-50" data-testid={`media-item-${m.id}`}>
                {m.type === "pdf" ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 p-2 text-xs text-center">PDF<span className="line-clamp-2 mt-1">{m.title}</span></div>
                ) : (
                  <img src={resolveImg(m.url)} alt={m.alt || m.title} className="w-full h-full object-cover" />
                )}
                <span className="absolute inset-0 bg-brass/0 group-hover:bg-brass/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Check className="text-navy bg-brass rounded-full p-1" size={26} /></span>
              </button>
            ))}
          </div>}
      </DialogContent>
    </Dialog>
  );
}

// Compact field: shows current image + pick/remove buttons
export function ImageField({ label, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid}>
      {label && <label className="block text-xs font-semibold text-navy mb-1.5 uppercase tracking-wide">{label}</label>}
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 rounded-sm border border-slate-200 overflow-hidden bg-slate-50 shrink-0">
          {value ? <img src={resolveImg(value)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">No image</div>}
        </div>
        <div className="flex flex-col gap-2">
          <button type="button" onClick={() => setOpen(true)} className="text-sm bg-navy text-ivory px-4 py-2 rounded-sm hover:bg-navy-light">Select / Replace</button>
          {value && <button type="button" onClick={() => onChange("")} className="text-sm text-red-500 flex items-center gap-1"><X size={13} /> Remove</button>}
        </div>
      </div>
      <MediaPicker open={open} onClose={() => setOpen(false)} onSelect={(m) => onChange(m.url)} type="image" />
    </div>
  );
}
